import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    ContactForm,
    buildCreateContactBody,
    TContactFormValues,
} from 'src/components/contacts/Form';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import useAuth from 'src/hooks/useAuth';
import { useContact, useWarboxConversation } from 'src/hooks';
import {
    CONTACT_DETAILS_ROUTE,
    CONVERSATION_CONTACT_UPDATE_FIELDS,
} from '../../constants/contactRegistration';
import { buildConversationContactInitialValues } from '../../utils/contactRegistration';

type RegisterContactDialogProps = {
    open: boolean;
    onClose: () => void;
    conversationId?: string;
    name?: string;
    phone?: string;
    storeId?: string;
};

export const RegisterContactDialog = ({
    open,
    onClose,
    conversationId,
    name,
    phone,
    storeId,
}: RegisterContactDialogProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { user } = useAuth();
    const { createContact } = useContact();
    const { updateConversation } = useWarboxConversation();
    const { customAlert } = SnackbarAlert();
    const initialValues = useMemo(
        () =>
            buildConversationContactInitialValues({
                name,
                phone,
                storeId,
            }),
        [name, phone, storeId]
    );

    const handleSubmit = async (values: TContactFormValues) => {
        if (!user?._id) return;

        const contact = await createContact(
            buildCreateContactBody(values, { createdBy: user._id })
        );

        if (!contact?._id) return;

        if (conversationId) {
            await updateConversation(
                {
                    [CONVERSATION_CONTACT_UPDATE_FIELDS.displayName]:
                        contact.name,
                    [CONVERSATION_CONTACT_UPDATE_FIELDS.phone]:
                        contact.phone || values.phone,
                    [CONVERSATION_CONTACT_UPDATE_FIELDS.linkedContact]:
                        contact._id,
                },
                conversationId
            );
        }

        customAlert(t('SnackBar.ContactCreated'), 'success');
        onClose();
        history.push(`${CONTACT_DETAILS_ROUTE}/${contact._id}`);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{String(t('contacts.Create'))}</DialogTitle>
            <DialogContent>
                <ContactForm
                    mode="create"
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    isPhoneRequired
                    loading={false}
                />
            </DialogContent>
        </Dialog>
    );
};
